import { useCallback, useMemo } from 'react'
import {
  ColDef,
  ColGroupDef,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  MenuItemDef,
} from 'ag-grid-community'
import {
  columnTypes,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { OrganizationWorkingDayCalendarDetail } from '../../../../lib/functions/organizationWorkingDayCalendar'
import { DateVO, dateVoService } from '../../../../domain/value-object/DateVO'
import { dateV2Service } from '../../../../domain/value-object/DateV2'
import {
  DayOfWeek,
  TimeGrain,
  mapDayOfWeekToNumber,
} from '../../../../lib/functions/report'
import { WorkLoadUnitState } from '../../../hooks/useWorkloadUnit'
import { SprintReportRow } from '../model/sprintReportRow'
import { SprintReportPageState } from '../hooks/pageState'
import { SprintReportKey } from '../../../../lib/functions/sprintReport'
import { colorPalette } from '../../../style/colorPalette'
import { SprintReportSearchCondition } from '../hooks/searchCondition'
import { defaultColumns } from './defaultColumns'
import {
  createDateReportColumnDef,
  createTotalReportColumnDef,
} from './reportValueColumns'
import { createSprintReportContextMenuItems } from './contextMenu'

const TOTAL_ESTIMATED_HOUR_BG = colorPalette.monotone[1]

const grainStep = (
  grain: TimeGrain
): { amount: number; unit: 'day' | 'week' | 'month' } => {
  switch (grain) {
    case TimeGrain.DAY:
      return { amount: 1, unit: 'day' }
    case TimeGrain.WEEK:
      return { amount: 1, unit: 'week' }
    case TimeGrain.TWO_WEEKS:
      return { amount: 2, unit: 'week' }
    case TimeGrain.MONTH:
      return { amount: 1, unit: 'month' }
    default:
      return { amount: 1, unit: 'week' }
  }
}

const advanceDate = (date: DateVO, grain: TimeGrain): DateVO => {
  const step = grainStep(grain)
  const current = dateVoService.toDate(date)
  const next =
    step.unit === 'day'
      ? dateV2Service.addDays(current, step.amount)
      : step.unit === 'week'
        ? dateV2Service.addDays(current, step.amount * 7)
        : dateV2Service.addMonths(current, step.amount)
  return dateVoService.construct(next)
}

// 列の開始境界を timeGrain と startDayOfWeek に合わせて調整する。
// 旧 BulkSheet 版と同じく、WEEK/TWO_WEEKS は startDayOfWeek の曜日まで遡り、MONTH は月初に揃える。
const alignStart = (
  start: DateVO,
  grain: TimeGrain,
  startDayOfWeek: DayOfWeek
): DateVO => {
  if (grain === TimeGrain.WEEK || grain === TimeGrain.TWO_WEEKS) {
    const date = dateVoService.toDate(start)
    // Date#getDay は Sun=0..Sat=6、mapDayOfWeekToNumber は Mon=1..Sun=7 系のため
    // 差分を mod 7 で合わせて開始曜日まで遡る
    const dayOfWeek = date.getDay()
    const startDayOfWeekNum = mapDayOfWeekToNumber(startDayOfWeek)
    const fromDiff = (dayOfWeek - startDayOfWeekNum + 7) % 7
    return dateVoService.construct(dateV2Service.addDays(date, -fromDiff))
  }
  if (grain === TimeGrain.MONTH) {
    const date = dateVoService.toDate(start)
    return dateVoService.construct(dateV2Service.startOfMonth(date))
  }
  return start
}

const buildColumnDefs = (
  searchCondition: SprintReportSearchCondition,
  workingDayCalendars: OrganizationWorkingDayCalendarDetail[]
): (ColDef | ColGroupDef)[] => {
  const { dateTerm } = searchCondition
  if (!dateTerm.startDate || !dateTerm.endDate) {
    return [...defaultColumns]
  }

  const rawStartVo = dateVoService.construct(new Date(dateTerm.startDate))
  const endVo = dateVoService.construct(new Date(dateTerm.endDate))
  const startVo = alignStart(
    rawStartVo,
    searchCondition.timeGrain,
    searchCondition.startDayOfWeek
  )

  const dateColumns: (ColDef | ColGroupDef)[] = []
  let cursor = startVo
  while (dateVoService.diff(endVo, cursor) > 0) {
    const next = advanceDate(cursor, searchCondition.timeGrain)
    dateColumns.push(
      createDateReportColumnDef(
        { from: cursor, to: next },
        searchCondition,
        workingDayCalendars
      )
    )
    cursor = next
  }

  const totalColumn = createTotalReportColumnDef(
    { from: startVo, to: cursor },
    searchCondition,
    workingDayCalendars
  )

  return [...defaultColumns, totalColumn, ...dateColumns]
}

export type SprintReportGridOptionsApi = {
  gridOptions: GridOptions<SprintReportRow>
  refreshColumns: (
    searchCondition: SprintReportSearchCondition,
    workingDayCalendars: OrganizationWorkingDayCalendarDetail[]
  ) => void
}

export const useSprintReportGridOptions = (
  getContextMenuArgs: () => SprintReportSearchCondition,
  gridApi: GridApi | null,
  initialPageState: SprintReportPageState,
  initialWorkloadUnitState: WorkLoadUnitState
): SprintReportGridOptionsApi => {
  // 動的カラム更新は ag-grid v33+ 以降 setGridOption('columnDefs', ...) で行う必要があるため、
  // gridOptions 自体は再生成せず（参照を安定させ）、列定義は API 経由で差し替える。
  // pageState/workloadUnitState の値は親側の useEffect で context オブジェクトを mutate して反映する。
  const gridOptions: GridOptions<SprintReportRow> = useMemo(
    () => ({
      context: {
        pageState: initialPageState,
        workloadUnitState: initialWorkloadUnitState,
      },
      columnTypes: columnTypes(),
      components: frameworkComponents,
      defaultColDef: {
        sortable: false,
        filter: false,
        resizable: true,
        suppressHeaderMenuButton: true,
      },
      columnDefs: [...defaultColumns],
      onCellClicked: defaultOnCellClicked,
      // 各メンバーの「合計」行（TOTAL_ESTIMATED_HOUR）は背景を薄グレーにして集計行であることを示す
      getRowStyle: params => {
        if (params.data?.key === SprintReportKey.TOTAL_ESTIMATED_HOUR) {
          return { backgroundColor: TOTAL_ESTIMATED_HOUR_BG }
        }
        return undefined
      },
      getContextMenuItems: (
        params: GetContextMenuItemsParams<SprintReportRow>
      ): MenuItemDef[] =>
        createSprintReportContextMenuItems(params, getContextMenuArgs()),
    }),
    // 意図的に初期値のみで生成。pageState/workloadUnitState 更新は context mutate で反映する
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getContextMenuArgs]
  )

  const refreshColumns = useCallback(
    (
      searchCondition: SprintReportSearchCondition,
      workingDayCalendars: OrganizationWorkingDayCalendarDetail[]
    ) => {
      const newDefs = buildColumnDefs(searchCondition, workingDayCalendars)
      gridApi?.setGridOption('columnDefs', newDefs)
    },
    [gridApi]
  )

  return { gridOptions, refreshColumns }
}
